import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
} from '@mui/material';
import BenefitCard from './Components/BenefitCard';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddBankDetailsAction, getInsuranceDetails, updateBankDetailsAction } from './APIs/action';
import FirmDetails from './Components/FirmDetails';
import { compressImage, getOptimalCompressionSettings, validateImageType } from '../../Utils/imageCompression';
import BankDetailsForm from './Components/BankDetailsForm';
import { AccountType } from './constants';
import ConfirmationPopup from './Components/ConfirmationPopup';
import { validateAccountNumber, validateIfscCode } from '../../Utils';
import { UserFileDict } from '../../../utils/settings';
import { trackAnalyticsEvent } from '../../../utils/clevertap';
import { useSelector } from 'react-redux';

const AddBankDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useSelector(state => state.user?.userData);
  const insuranceType = location?.state?.insurance_type ;
  const selectedCompanyEntity = location?.state?.selectedCompanyEntity ;
  const isEdit = location?.state?.isEdit;
  const AccountData = location?.state?.account_data || {};
  const navigateTo = location?.state?.navigate_to;
  const [insuranceData, setInsuranceData] = useState([]);
  const { benefit_card, selected_firm, confirmation_text } = insuranceData;
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitLoader, setSubmitLoader] = useState(false);
  const accountTypeOptions = Object.values(AccountType).map(value => ({
    label: value,
    value: value,
  }));
  const [bankDetails, setBankDetails] = useState({
    account_number: '',
    ifsc_code:'',
    account_type: '',
    id_str_base64: null,
    file_name: null,
    confirm: false,
    id_type: UserFileDict.BANK_PASSBOOK,
  });
  
  // Track add bank details screen view
  useEffect(() => {
    trackAnalyticsEvent("BENEFICIARY_ADD_BANK_DETAILS_SCREEN_VIEW", {
      insurance_type: insuranceType,
      is_edit: isEdit,
      user_id: userData?.id,
      beneficiary_id: location?.state?.beneficiary_id
    });
  }, [insuranceType, isEdit, userData?.id]);
  
  const getAllInsuranceDetails = async (insuranceType, selectedCompanyEntity) => {
    setLoader(true);
    const { data, error, message } = await getInsuranceDetails(insuranceType, selectedCompanyEntity);
    setLoader(false);
    if (!error) {
      setInsuranceData(data);
    } else {
      toast.error(message)
    }   
  };
  useEffect(() => {
    if (insuranceType && selectedCompanyEntity) {
      getAllInsuranceDetails(insuranceType, selectedCompanyEntity);
    }else{
      navigate('/beneficiary');
    }
  }, [insuranceType, selectedCompanyEntity]);
  const [compressedSize, setCompressedSize] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) {
      setBankDetails((prev) => ({
        ...prev,
        id_str_base64: null,
        file_name: null,
      }));
      return;
    }

    const file = files[0];
    
    // Track file upload start
    trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_FILE_UPLOAD_ATTEMPT", {
      insurance_type: insuranceType,
      file_type: file.type,
      file_size: file.size,
      user_id: userData?.id
    });
    
    const acceptedFileTypes = ['image/png', 'image/jpeg', 'image/heic'];
    if (!validateImageType(file, acceptedFileTypes)) {
      toast.error('Invalid file type. Please upload PNG, JPG and JPEG.');
      trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_FILE_UPLOAD_ERROR", {
        insurance_type: insuranceType,
        error_type: "invalid_file_type",
        file_type: file.type,
        user_id: userData?.id
      });
      return;
    }

    const filename = file.name;
    setIsCompressing(true);
    try {
      const optimalSettings = await getOptimalCompressionSettings();
      const compressionOptions = {
        maxWidth: 1024,
        maxHeight: 1024,
        quality: 0.7,
        ...optimalSettings,
      };

      const result = await compressImage(file, compressionOptions);

      setBankDetails((prev) => ({
        ...prev,
        id_str_base64: result.base64Data,
        file_name: filename,
      }));
      setCompressedSize(result.compressedSize);
      
      // Track successful file upload
      trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_FILE_UPLOAD_SUCCESS", {
        insurance_type: insuranceType,
        file_type: file.type,
        original_size: file.size,
        compressed_size: result.compressedSize,
        user_id: userData?.id
      });
    } catch (error) {
      toast.error('Failed to upload image. Try again.');
      trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_FILE_UPLOAD_ERROR", {
        insurance_type: insuranceType,
        error_type: "compression_failed",
        file_type: file.type,
        user_id: userData?.id
      });
    } finally {
      setIsCompressing(false);
    }
  };
  const resetBankDetails = () => {
    setBankDetails((prevState) => ({
      ...prevState,
      id_str_base64: null,
      file_name:null,
      account_number: '',
      ifsc_code:'',
      account_type: '',
      confirm: false,
    }));
  };
  const validateFields = () => {
    const newErrors = {};

    if (!bankDetails.account_number){ 
      newErrors.account_number = 'Account number is required'
    } else if (!validateAccountNumber(bankDetails.account_number)) {
      newErrors.account_number = 'Invalid account number'};

    if (!bankDetails.ifsc_code) { 
      newErrors.ifsc_code = 'IFSC code is required'
    } else if(!validateIfscCode(bankDetails.ifsc_code)) {
      newErrors.ifsc_code = 'Invalid IFSC code';
    };

    if (!bankDetails.account_type) {
      newErrors.account_type = 'Account type is required';
    }
    
    if (!bankDetails.id_str_base64) {
      newErrors.id_str_base64 = 'Cancelled cheque image is required';
    }
    
    if(!bankDetails.confirm) newErrors.confirm = 'Please confirm the details are correct';
    
    // Track validation errors if any
    if (Object.keys(newErrors).length > 0) {
      trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_VALIDATION_ERROR", {
        insurance_type: insuranceType,
        error_fields: Object.keys(newErrors),
        user_id: userData?.id
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  useEffect(() => {
    const fetchData = async () => {
      if (AccountData?.details?.length) {
        const detailsMap = {};
        for (const item of AccountData.details) {
          const { display_name, display_value } = item;
          if (display_name === "Bank Account Number") {
            detailsMap.account_number = display_value;
          } else if (display_name === "IFSC") {
            detailsMap.ifsc_code = display_value;
          }else if (display_name === 'Account Type') {
            const matchedOwner = accountTypeOptions.find(option => option.value === display_value);
            if (matchedOwner) {
              detailsMap.account_type = matchedOwner.value;
            }
          }
        }
        setBankDetails(prev => ({
          ...prev,
          ...detailsMap,
          id_str_base64: AccountData?.uploaded_image?.id_str_base64 || null,
          file_name: AccountData?.uploaded_image?.file_name || null,
          confirm: true
        }));
      }
    };
    if (isEdit) {
      fetchData();
    }
  }, [isEdit, AccountData]);
  const isSameFile = (
    bankDetails.id_str_base64 === AccountData?.uploaded_image?.id_str_base64 &&
    bankDetails.file_name === AccountData?.uploaded_image?.file_name
  );
  const handleSubmit = async(e) => {
    // e.preventDefault();
    setOpenConfirmModal(false);
    
    // Track form submission attempt
    trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_FORM_SUBMIT", {
      insurance_type: insuranceType,
      is_edit: isEdit,
      user_id: userData?.id,
      account_type: bankDetails.account_type
    });
    
    const payload = {
      id_str_base64: isSameFile ? null : bankDetails.id_str_base64 || null,
      file_name: isSameFile ? null : bankDetails.file_name || null,
      account_number: bankDetails.account_number,
      ifsc_code:bankDetails.ifsc_code,
      account_type: bankDetails.account_type,
      id_type: bankDetails.id_type,
      company_esi_id: selectedCompanyEntity,
    };
    setSubmitLoader(true);
    let response;
    if (isEdit) {
      response = await updateBankDetailsAction(payload);
    } else {
      response = await AddBankDetailsAction(payload);
    }
    setSubmitLoader(false);
    const { error, message } = response;
    if (!error) {
      toast.success(message);
      
      // Track successful submission
      trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_FORM_SUBMIT_SUCCESS", {
        insurance_type: insuranceType,
        is_edit: isEdit,
        user_id: userData?.id,
        account_type: bankDetails.account_type
      });
      
      navigate('/beneficiary/detail', {
        state: {
          insurance_type: insuranceType,
          selectedCompanyEntity: selectedCompanyEntity,
          beneficiary_flow: 'BANK_ACCOUNT_VERFIFICATION'
        },
      });
    } else {
      toast.error(message);
      
      // Track submission error
      trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_FORM_SUBMIT_ERROR", {
        insurance_type: insuranceType,
        is_edit: isEdit,
        user_id: userData?.id,
        error_message: message
      });
    }
  }

  const handleChange = (field, value) => {
    setBankDetails((prev) => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
  }; 

  return (
    <Box sx={{ p: { xs:1.5, md:3 }, borderRadius: 2 }}>
      {!loader ? (
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Grid container spacing={2} sx={{ alignItems: 'stretch', mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <BenefitCard item={benefit_card} sx={{ height: '100%' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ border: '1px solid #D9D9D9', p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
                {selected_firm && selected_firm.map((detail, i) => (
                  <React.Fragment key={i}>
                    <FirmDetails key={i} detail={detail} />
                  </React.Fragment>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      ):(
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3}}>
          <CircularProgress size={24} sx={{ color: '#FFA525' }} />
        </Box>
      )}
      <Box sx={{ p: 3, mb: 2, border: '1px solid #E0E0E0', borderRadius: 3 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '20px', mb: 3, lineHeight: '1.8' }}>
          Beneficiary Account Details
        </Typography>
        {insuranceData?.note && (
          <Typography variant='p3' sx={{mb: 3}}>
            {insuranceData?.note}
          </Typography>
        )}
        
        <BankDetailsForm
          person={bankDetails}
          setPerson={(field, value) => handleChange(field, value)}
          account_type={accountTypeOptions}
          handleUpload={handleUpload}
          confirmation_text={confirmation_text}
          errors={errors}
          isEdit={isEdit}
        />
        <Box sx={{ display: 'flex', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={()=>{
              if (validateFields()) {
                setOpenConfirmModal(true);
                trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_FORM_SUBMIT_CLICK", {
                  insurance_type: insuranceType,
                  is_edit: isEdit,
                  user_id: userData?.id,
                  account_type: bankDetails.account_type
                });
              }
            }}
            sx={{ textTransform: 'none', px: 3, position: 'relative', width: {xs: '100%', md:'fit-content'} }}
          >
            {submitLoader ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Verify'
            )}
          </Button>
        </Box>
        <ConfirmationPopup
          open={openConfirmModal}
          onClose={() => {
            setOpenConfirmModal(false);
            trackAnalyticsEvent("BENEFICIARY_BANK_DETAILS_CONFIRMATION_MODAL_CLOSE", {
              insurance_type: insuranceType,
              is_edit: isEdit,
              user_id: userData?.id
            });
          }}
          onSubmit={handleSubmit}
          data={bankDetails}
        />
      </Box>
    </Box>
  );
};


export default AddBankDetails